import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Stack,
    Step,
    StepConnector,
    StepLabel,
    Stepper,
    TextField,
    Tooltip,
    Typography,
    alpha,
    styled,
    stepConnectorClasses,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import ActionMenu from './action-menu';

const XERO_BLUE = 'var(--color-primary)';

const ThinConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.vertical}`]: { marginLeft: 11 },
    [`& .${stepConnectorClasses.line}`]: {
        borderColor: theme.palette.divider,
        borderLeftWidth: 1,
        minHeight: 24,
    },
}));

function XeroLogo({ size = 32 }: { size?: number }) {
    return (
        <Box sx={{
            width: size, height: size, borderRadius: 1,
            bgcolor: XERO_BLUE, display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
            <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 24 24" fill="white">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm3.5 13.5l-2.1-3.5 2.1-3.5h-1.9l-1.1 2-1.1-2H9.5l2.1 3.5-2.1 3.5h1.9l1.1-2 1.1 2h1.9z" />
            </svg>
        </Box>
    );
}

type BankAccountOption = { id: number | null; name: string };
const NO_BANK_ACCOUNT_OPTION: BankAccountOption = { id: null, name: 'No bank account' };

// Xero's own AccountIDs are UUID strings, not our local numeric ids.
type XeroBankAccountOption = { id: string | null; name: string };
const NO_XERO_BANK_ACCOUNT_OPTION: XeroBankAccountOption = { id: null, name: 'No bank account' };

type XeroConnectedViewProps = {
    organisationName?: string;
    lastGLSyncAt?: string | null;
    lastTransactionSyncAt?: string | null;
    lastStatusesSyncAt?: string | null;
    lastInvoicesSyncAt?: string | null;
    lastClientsSyncAt?: string | null;
    syncResults?: Record<string, { status: string | null; detail: string | null }>;
    importStatusText?: string | null;
    onDisconnect?: () => void;
    onSyncGL?: () => void;
    onSyncTransactions?: () => void;
    onSyncStatuses?: () => void;
    onSyncInvoices?: () => void;
    onSyncClients?: () => void;
    syncingGL?: boolean;
    syncingTransactions?: boolean;
    syncingStatuses?: boolean;
    syncingInvoices?: boolean;
    syncingClients?: boolean;
    statusesSynced?: boolean;
    // Destination: which of our own bank accounts synced transactions get
    // filed under.
    bankAccounts?: BankAccountOption[];
    selectedBankAccountId?: number | null;
    onBankAccountChange?: (id: number | null) => void;
    // Source: which of Xero's own bank accounts to pull transactions from.
    // Xero has no server-side way to scope /BankTransactions to one account,
    // so without this every connected account's transactions (savings,
    // credit cards, etc.) get pulled in and filed under whichever single
    // destination account is selected above.
    xeroBankAccounts?: XeroBankAccountOption[];
    selectedXeroBankAccountId?: string | null;
    onXeroBankAccountChange?: (id: string | null) => void;
    // Optional backfill start date (YYYY-MM-DD) for Sync Transactions/Invoices -
    // without it, syncs are incremental from the last successful sync, so
    // records from before that point (e.g. missed by a bug that's since been
    // fixed) won't reappear unless a date is set here.
    fromDate?: string;
    onFromDateChange?: (value: string) => void;
    canSync?: boolean;
    canConnect?: boolean;
};

type XeroConnectViewProps = {
    onConnect?: () => void;
    connecting?: boolean;
    pending?: boolean;
    canConnect?: boolean;
}

export function XeroConnectView({ onConnect, connecting, pending, canConnect = true }: XeroConnectViewProps) {
    const steps = [
        { label: 'Click "Connect Xero"', description: "You'll be redirected to Xero's secure login page." },
        { label: 'Log in and authorise', description: 'Select your organisation and approve read-only access.' },
        { label: "You're done", description: 'Your data syncs automatically every night — no manual exports needed.' },
    ];

    return (
        <Stack spacing={3}>
            <Stack direction="row" spacing={1.5} alignItems="center">
                <XeroLogo size={36} />
                <Box>
                <Typography variant="subtitle1" fontWeight={500}>Connect your Xero account</Typography>
                <Typography variant="body2" color="text.secondary">
                    Securely link your accounting data for cash flow forecasting
                </Typography>
                </Box>
            </Stack>
            <Divider />
            <Box>
                <Typography variant="overline" color="text.secondary" display="block" gutterBottom>
                What we sync from Xero
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                {[
                    { icon: <FormatListBulletedIcon fontSize="small" />, title: 'GL codes', description: 'Your full chart of accounts used to categorise cash flows' },
                    { icon: <ReceiptLongIcon fontSize="small" />, title: 'Transactions', description: 'Bank transactions, sales invoices, and purchase bills' },
                ].map((item) => (
                    <Card variant="outlined" key={item.title} sx={{ height: '100%' }}>
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                            <Box sx={{ color: XERO_BLUE }}>{item.icon}</Box>
                            <Typography variant="body2" fontWeight={500}>{item.title}</Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">{item.description}</Typography>
                        </CardContent>
                    </Card>
                ))}
                </Box>
            </Box>
            <Box>
                <Typography variant="overline" color="text.secondary" display="block" gutterBottom>
                    How it works
                </Typography>
                <Stepper orientation="vertical" connector={<ThinConnector />} sx={{ pl: 0 }}>
                {steps.map((step, i) => (
                    <Step key={step.label} active>
                        <StepLabel
                            StepIconComponent={() => (
                            <Box sx={{
                                width: 24, height: 24, borderRadius: '50%',
                                bgcolor: 'rgba(var(--color-primary-rgb), 0.12)', color: XERO_BLUE,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 11, fontWeight: 600, flexShrink: 0,
                            }}>
                                {i + 1}
                            </Box>
                            )}
                        >
                            <Typography variant="body2" fontWeight={500}>{step.label}</Typography>
                            <Typography variant="caption" color="text.secondary">{step.description}</Typography>
                        </StepLabel>
                    </Step>
                ))}
                </Stepper>
            </Box>
            <Box sx={{
                display: 'flex', gap: 1.5, alignItems: 'flex-start',
                bgcolor: (t) => alpha(t.palette.success.main, 0.06),
                border: '1px solid', borderColor: (t) => alpha(t.palette.success.main, 0.2),
                borderRadius: 2, px: 2, py: 1.5,
            }}>
                <ShieldOutlinedIcon sx={{ color: 'success.main', fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                <Typography variant="caption" color="success.dark" lineHeight={1.6}>
                    We never see your Xero password. Access is read-only and can be revoked at any time from your settings.
                </Typography>
            </Box>
            <Button
                variant="contained"
                size="large"
                onClick={onConnect}
                disabled={connecting || pending || !canConnect}
                sx={{
                    bgcolor: XERO_BLUE, '&:hover': { bgcolor: '#009681' },
                    textTransform: 'none', fontWeight: 500, borderRadius: 1,
                    color: '#fff'
                }}
                fullWidth
            >
                {connecting ? 'Connecting…' : pending ? 'Waiting for authorisation…' : 'Connect Xero'}
            </Button>
        </Stack>
    );
}

export function XeroConnectedView({
    organisationName = 'ACME Pty Ltd',
    lastGLSyncAt = null,
    lastTransactionSyncAt = null,
    lastStatusesSyncAt = null,
    lastInvoicesSyncAt = null,
    lastClientsSyncAt = null,
    syncResults = {},
    importStatusText = null,
    onDisconnect,
    onSyncGL,
    onSyncTransactions,
    onSyncStatuses,
    onSyncInvoices,
    onSyncClients,
    syncingGL = false,
    syncingTransactions = false,
    syncingStatuses = false,
    syncingInvoices = false,
    syncingClients = false,
    statusesSynced = false,
    bankAccounts = [],
    selectedBankAccountId = null,
    onBankAccountChange,
    xeroBankAccounts = [],
    selectedXeroBankAccountId = null,
    onXeroBankAccountChange,
    fromDate = '',
    onFromDateChange,
    canSync = true,
    canConnect = true,
}: XeroConnectedViewProps) {
    const fmtDate = (iso?: string | null) =>
        iso ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso)) : '-';

    return (
        <Stack spacing={2.5}>
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <XeroLogo size={36} />
                    <Box>
                        <Typography variant="subtitle1" fontWeight={500}>{organisationName}</Typography>
                        <Typography sx={{ fontSize: 11, fontStyle: 'italic', color: '#009681' }}>Connected</Typography>
                    </Box>
                </Stack>
            </Stack>
            <Divider sx={{ my: 0.5 }} />
            <Box>
                <Typography variant="overline" color="text.secondary" display="block" gutterBottom>
                    Sync data from Xero
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.5, alignItems: 'stretch' }}>
                {[
                    { key: 'gl', icon: <FormatListBulletedIcon fontSize="small" />, label: 'GL codes', date: lastGLSyncAt },
                    { key: 'transactions', icon: <ReceiptLongIcon fontSize="small" />, label: 'Transactions', date: lastTransactionSyncAt },
                    { key: 'statuses', icon: <SyncIcon fontSize="small" />, label: 'Statuses', date: lastStatusesSyncAt },
                    { key: 'invoices', icon: <ReceiptLongIcon fontSize="small" />, label: 'Invoices', date: lastInvoicesSyncAt },
                    { key: 'clients', icon: <SyncIcon fontSize="small" />, label: 'Clients', date: lastClientsSyncAt },
                ].map((item) => {
                    const result = syncResults[item.key];
                    const isFailed = result?.status === 'failed';
                    // Each card's "Sync Now" action + its own gating/tooltip -
                    // same disabled conditions and messages the old per-card
                    // buttons used, just surfaced through the ellipsis menu
                    // instead of a visible button.
                    const syncNowAction = (() => {
                        switch (item.key) {
                            case 'gl':
                                return { label: 'Sync Now', icon: <SyncIcon fontSize="small" />, onClick: onSyncGL, disabled: syncingGL || !canSync };
                            case 'statuses':
                                return { label: 'Sync Now', icon: <SyncIcon fontSize="small" />, onClick: onSyncStatuses, disabled: syncingStatuses || !canSync };
                            case 'clients':
                                return { label: 'Sync Now', icon: <SyncIcon fontSize="small" />, onClick: onSyncClients, disabled: syncingClients || !canSync };
                            case 'transactions':
                                return {
                                    label: 'Sync Now',
                                    icon: <SyncIcon fontSize="small" />,
                                    onClick: onSyncTransactions,
                                    disabled: syncingTransactions || !lastGLSyncAt || !selectedBankAccountId || !selectedXeroBankAccountId || !canSync,
                                    tooltip: !lastGLSyncAt ? 'Sync GL Codes first before syncing transactions' : !selectedXeroBankAccountId ? 'Select a source bank account first' : !selectedBankAccountId ? 'Select a destination bank account first' : undefined,
                                };
                            case 'invoices':
                                return {
                                    label: 'Sync Now',
                                    icon: <ReceiptLongIcon fontSize="small" />,
                                    onClick: onSyncInvoices,
                                    disabled: syncingInvoices || !statusesSynced || !selectedBankAccountId || !canSync,
                                    tooltip: !statusesSynced ? 'Sync Statuses first before syncing invoices' : !selectedBankAccountId ? 'Select a bank account first' : undefined,
                                };
                            default:
                                return null;
                        }
                    })();

                    return (
                        <Card variant="outlined" key={item.key} sx={{ display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ py: 1.5, px: 2, display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1, '&:last-child': { pb: 1.5 } }}>
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Box sx={{ color: 'text.secondary' }}>{item.icon}</Box>
                                        <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                                    </Stack>
                                    {syncNowAction && <ActionMenu actions={[syncNowAction]} />}
                                </Stack>
                                <Box>
                                    <Typography variant="body2" fontWeight={500}>{fmtDate(item.date)}</Typography>
                                    {result?.detail && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                mt: 0.5,
                                                color: isFailed ? 'error.main' : 'text.secondary',
                                            }}
                                            title={result.detail}
                                        >
                                            {result.detail}
                                        </Typography>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    );
                })}
                </Box>
            </Box>

            {/* Shared inputs for Transactions/Invoices syncs - kept in one
                place rather than duplicated inside both cards (Backfill from
                date applies to both; the bank account selectors only affect
                Transactions but live here too since they're the same kind of
                shared "sync settings", not a per-card status). */}
            <Box>
                <Typography variant="overline" color="text.secondary" display="block" gutterBottom>
                    Sync settings
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Tooltip
                        title="Xero has no way to scope a sync to a single bank account - without picking one here, transactions from every connected account (savings, credit cards, etc.) get pulled in together."
                        placement="top"
                        arrow
                    >
                        <Autocomplete<XeroBankAccountOption, false, true, false>
                            options={[NO_XERO_BANK_ACCOUNT_OPTION, ...xeroBankAccounts]}
                            getOptionLabel={(a) => a.name}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            value={[NO_XERO_BANK_ACCOUNT_OPTION, ...xeroBankAccounts].find((a) => a.id === selectedXeroBankAccountId) ?? NO_XERO_BANK_ACCOUNT_OPTION}
                            onChange={(_, val) => onXeroBankAccountChange?.(val?.id ?? null)}
                            disableClearable
                            size="small"
                            sx={{ flex: 1 }}
                            renderInput={(params) => (
                                <TextField {...params} label="Source bank account (Xero)" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }} />
                            )}
                        />
                    </Tooltip>
                    <Autocomplete<BankAccountOption, false, true, false>
                        options={[NO_BANK_ACCOUNT_OPTION, ...bankAccounts]}
                        getOptionLabel={(a) => a.name}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        value={[NO_BANK_ACCOUNT_OPTION, ...bankAccounts].find((a) => a.id === selectedBankAccountId) ?? NO_BANK_ACCOUNT_OPTION}
                        onChange={(_, val) => onBankAccountChange?.(val?.id ?? null)}
                        disableClearable
                        size="small"
                        sx={{ flex: 1 }}
                        renderInput={(params) => (
                            <TextField {...params} label="Destination bank account" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }} />
                        )}
                    />
                    <Tooltip
                        title="Optionally select a start date to backfill transactions/invoices from before the last successful sync. Leave blank to sync from the last successful sync date."
                        placement="top"
                        arrow
                    >
                        <Box sx={{ flex: 1 }}>
                            <DatePicker
                                label="Backfill from date"
                                value={fromDate ? dayjs(fromDate) : null}
                                onChange={(value) => onFromDateChange?.(value && dayjs.isDayjs(value) && value.isValid() ? value.format('YYYY-MM-DD') : '')}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                        </Box>
                    </Tooltip>
                </Stack>
            </Box>

            {/* GL + Disconnect row */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>

                <Button
                    variant="outlined"
                    color="error"
                    onClick={onDisconnect}
                    disabled={!canConnect}
                    sx={{ textTransform: 'none', borderRadius: 1, flex: 1 }}
                >
                    Disconnect
                </Button>
            </Stack>

            {importStatusText && (
                <Box sx={{
                    display: 'flex', gap: 1, alignItems: 'center',
                    bgcolor: (t) => alpha(t.palette.info.main, 0.06),
                    border: '1px solid', borderColor: (t) => alpha(t.palette.info.main, 0.2),
                    borderRadius: 1, px: 2, py: 1.25,
                }}>
                    <SyncIcon sx={{ fontSize: 14, color: 'info.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="info.dark">{importStatusText}</Typography>
                </Box>
            )}
        </Stack>
    );
}
